import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const ADMIN_TOKEN_KEY = "pm_poshan_admin_token";
const USER_TOKEN_KEY = "pm_poshan_user_token";

export const auth = {
  getToken: () => localStorage.getItem(ADMIN_TOKEN_KEY) || "",
  setToken: (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t),
  clear: () => localStorage.removeItem(ADMIN_TOKEN_KEY),
  isAuthed: () => !!localStorage.getItem(ADMIN_TOKEN_KEY),
};

export const userAuth = {
  getToken: () => localStorage.getItem(USER_TOKEN_KEY) || "",
  setToken: (t) => localStorage.setItem(USER_TOKEN_KEY, t),
  clear: () => localStorage.removeItem(USER_TOKEN_KEY),
  isAuthed: () => !!localStorage.getItem(USER_TOKEN_KEY),
};

const bare = axios.create({ baseURL: API_BASE, headers: { "Content-Type": "application/json" } });
const authed = axios.create({ baseURL: API_BASE, headers: { "Content-Type": "application/json" } });
authed.interceptors.request.use((cfg) => {
  const t = auth.getToken();
  if (t) cfg.headers["Authorization"] = `Bearer ${t}`;
  return cfg;
});
authed.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) auth.clear();
    return Promise.reject(err);
  }
);

// User-authenticated axios (Phase 1 SaaS)
const userAxios = axios.create({ baseURL: API_BASE, headers: { "Content-Type": "application/json" } });
userAxios.interceptors.request.use((cfg) => {
  const t = userAuth.getToken();
  if (t) cfg.headers["Authorization"] = `Bearer ${t}`;
  return cfg;
});
userAxios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) userAuth.clear();
    return Promise.reject(err);
  }
);

export const api = {
  health: () => bare.get("/health").then((r) => r.data),

  // ---- Public: overrides used by the calculator ----
  listRateOverrides: () => bare.get("/rates/overrides").then((r) => r.data),
  getLaddooOverride: () => bare.get("/laddoo/overrides").then((r) => r.data),
  getMenuOverride: () => bare.get("/menu/overrides").then((r) => r.data),

  // ---- Admin ----
  adminLogin: (username, password) => bare.post("/admin/login", { username, password }).then((r) => r.data),
  adminMe: () => authed.get("/admin/me").then((r) => r.data),
  saveRateOverride: (academic_year, rates) =>
    authed.put(`/admin/rates/${academic_year}`, { rates }).then((r) => r.data),
  getRateOverride: (academic_year) =>
    bare.get(`/rates/overrides/${academic_year}`).then((r) => r.data),
  deleteRateOverride: (academic_year) =>
    authed.delete(`/admin/rates/${academic_year}`).then((r) => r.data),
  saveLaddoo: (effective_periods) => authed.put("/admin/laddoo", { effective_periods }).then((r) => r.data),
  saveMenu: (menu_plans) => authed.put("/admin/menu", { menu_plans }).then((r) => r.data),

  // ---- Laddoo recipe ----
  getLaddooRecipe: () => bare.get("/laddoo/recipe").then((r) => r.data),
  saveLaddooRecipe: (ingredients) => authed.put("/admin/laddoo/recipe", { ingredients }).then((r) => r.data),
  resetLaddooRecipe: () => authed.delete("/admin/laddoo/recipe").then((r) => r.data),

  // ---- Poshan Panji (public) ----
  savePanjiEntry: (entry) => bare.post("/poshan-panji/entries", entry).then((r) => r.data),
  listPanjiEntries: (udise, from_date, to_date) =>
    bare.get("/poshan-panji/entries", { params: { udise, from_date, to_date } }).then((r) => r.data),
  // ---- User Auth (Phase 1) ----
  registerUser: (payload) => bare.post("/auth/register", payload).then((r) => r.data),
  loginUser: (mobile, password) => bare.post("/auth/login", { mobile, password }).then((r) => r.data),
  authMe: () => userAxios.get("/auth/me").then((r) => r.data),
  authUpdateMe: (patch) => userAxios.patch("/auth/me", patch).then((r) => r.data),
  changePassword: (current_password, new_password) => userAxios.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  setupRecovery: (numbers, image_ids) => userAxios.post("/auth/recovery/setup", { numbers, image_ids }).then((r) => r.data),
  recoveryLibrary: () => bare.get("/auth/recovery-library").then((r) => r.data),
  personalizeOptions: () => userAxios.get("/auth/personalize-options").then((r) => r.data),
  personalize: (lucky_number, avatar_id) => userAxios.post("/auth/personalize", { lucky_number, avatar_id }).then((r) => r.data),
  forgotStart: (mobile) => bare.post("/auth/forgot/start", { mobile }).then((r) => r.data),
  forgotVerify: (session_token, numbers, image_ids) => bare.post("/auth/forgot/verify", { session_token, numbers, image_ids }).then((r) => r.data),
  forgotReset: (reset_token, new_password) => bare.post("/auth/forgot/reset", { reset_token, new_password }).then((r) => r.data),

  // ---- Referrals ----
  referralsMy: () => userAxios.get("/referrals/my").then((r) => r.data),
  referralsValidate: (code) => bare.post("/referrals/validate-code", { code }).then((r) => r.data),
  rechargeRequest: (payload) => userAxios.post("/referrals/recharge-request", payload).then((r) => r.data),
  rechargeStatus: () => userAxios.get("/referrals/recharge-request").then((r) => r.data),
  adminReferrals: (params = {}) => authed.get("/admin/referrals", { params }).then((r) => r.data),
  adminReferralAnalytics: () => authed.get("/admin/referrals/analytics").then((r) => r.data),
  adminReferralReview: (id, payload) => authed.post(`/admin/referrals/${id}/review`, payload).then((r) => r.data),
  adminRechargeRequests: (params = {}) => authed.get("/admin/recharge-requests", { params }).then((r) => r.data),
  adminRechargeUpdate: (id, payload) => authed.put(`/admin/recharge-requests/${id}`, payload).then((r) => r.data),
  // ---- Analytics ----
  analyticsTrack: (payload) => bare.post("/analytics/track", payload).then((r) => r.data).catch(() => null),
  analyticsSummary: () => authed.get("/admin/analytics/summary").then((r) => r.data),

  // ---- Campaign config ----
  campaignConfig: () => bare.get("/campaign/config").then((r) => r.data).catch(() => null),
  adminCampaignConfig: () => authed.get("/admin/campaign/config").then((r) => r.data),
  adminCampaignSave: (payload) => authed.put("/admin/campaign/config", payload).then((r) => r.data),

  // ---- Coupons ----
  validateCoupon: (code) => bare.post("/subscription/validate-coupon", { code }).then((r) => r.data),
  adminCoupons: () => authed.get("/admin/coupons").then((r) => r.data),
  adminUpsertCoupon: (payload) => authed.post("/admin/coupons", payload).then((r) => r.data),
  adminDeleteCoupon: (code) => authed.delete(`/admin/coupons/${code}`).then((r) => r.data),

  // ---- Live counter (public) ----
  premiumCount: () => bare.get("/subscription/premium-count").then((r) => r.data),

  // ---- Ads ----
  adsActive: (placement) => bare.get("/ads/active", { params: placement ? { placement } : {} }).then((r) => r.data),
  adImpression: (campaign_id, placement, session_id) => bare.post("/ads/impression", { campaign_id, placement, session_id }).then((r) => r.data).catch(() => null),
  adClick: (campaign_id, placement, session_id) => bare.post("/ads/click", { campaign_id, placement, session_id }).then((r) => r.data).catch(() => null),
  adminAds: () => authed.get("/admin/ads").then((r) => r.data),
  adminUpsertAd: (payload) => authed.post("/admin/ads", payload).then((r) => r.data),
  adminAdAnalytics: () => authed.get("/admin/ads/analytics").then((r) => r.data),

  // ---- Subscription (Phase 3) ----
  subConfig: () => bare.get("/subscription/config").then((r) => r.data),
  subMe: () => userAxios.get("/subscription/me").then((r) => r.data),
  createSubOrder: (coupon_code) => userAxios.post("/subscription/create-order", coupon_code ? { coupon_code } : {}).then((r) => r.data),
  verifySubPayment: (payload) => userAxios.post("/subscription/verify", payload).then((r) => r.data),

  // ---- Monthly MDCF (Premium) ----
  mdcfGet:    (year, month) => userAxios.get("/monthly-mdcf", { params: { year, month } }).then((r) => r.data),
  mdcfSave:   (payload) => userAxios.post("/monthly-mdcf", payload).then((r) => r.data),
  mdcfReport: (year, month) => userAxios.get("/monthly-mdcf/report", { params: { year, month } }).then((r) => r.data),
  mdcfSchoolProfileGet:  () => userAxios.get("/monthly-mdcf/school-profile").then((r) => r.data),
  mdcfSchoolProfileSave: (payload) => userAxios.post("/monthly-mdcf/school-profile", payload).then((r) => r.data),
  mdcfCopyFromPrev: (year, month) => userAxios.post("/monthly-mdcf/copy-from-previous", null, { params: { year, month } }).then((r) => r.data),
  mdcfPdf: (year, month) => userAxios.get("/monthly-mdcf/pdf", { params: { year, month }, responseType: "blob" })
    .then((r) => ({ blob: r.data, filename: (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1] || `PM_POSHAN_MDCF_${year}_${month}.pdf` })),

  // ---- Daily entries (Phase 2, premium-gated) ----
  saveDailyEntry: (entry) => userAxios.post("/daily/entries", entry).then((r) => r.data),
  listDailyEntries: (params = {}) => userAxios.get("/daily/entries", { params }).then((r) => r.data),
  getDailyEntry: (id) => userAxios.get(`/daily/entries/${id}`).then((r) => r.data),
  deleteDailyEntry: (date_iso, entry_type = "meal") => userAxios.delete("/daily/entries", { params: { date_iso, entry_type } }).then((r) => r.data),
  dailySummary: (month) => userAxios.get("/daily/summary", { params: month ? { month } : {} }).then((r) => r.data),

  // ---- Super Admin (Phase 5) ----
  adminAnalytics: () => authed.get("/admin/analytics").then((r) => r.data),
  adminUsers: (params = {}) => authed.get("/admin/users", { params }).then((r) => r.data),
  adminUserDetail: (id) => authed.get(`/admin/users/${id}`).then((r) => r.data),
  adminUserAction: (id, payload) => authed.post(`/admin/users/${id}/action`, payload).then((r) => r.data),
  adminPayments: (params = {}) => authed.get("/admin/payments", { params }).then((r) => r.data),
  adminSubscriptions: (params = {}) => authed.get("/admin/subscriptions", { params }).then((r) => r.data),
  adminAuditLogs: (params = {}) => authed.get("/admin/audit-logs", { params }).then((r) => r.data),

  // ---- Free-report-limit (Phase 6/8) ----
  reportUsage: () => {
    // Send bearer if logged in, else anonymous (IP-based)
    const t = userAuth.getToken();
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    return bare.get("/reports/usage", { headers }).then((r) => r.data);
  },
  reportCheckAndIncrement: () => {
    const t = userAuth.getToken();
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    return bare.post("/reports/check-and-increment", null, { headers }).then((r) => r.data);
  },
};

export default api;
